/* eslint-disable react-refresh/only-export-components */
// `nodeTypes` is imported by AgentExecutionGraph.jsx; moving it to a new file is not worth it.
import { Handle, Position } from "reactflow";
import {
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Tooltip,
  ThemeIcon,
  NumberFormatter,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBrain,
  IconClock,
  IconCoin,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconTool,
} from "@tabler/icons-react";

/**
 * Custom node component for agent execution visualization.
 */
export function AgentExecutionNodeComponent({ data }) {
  const theme = useMantineTheme();

  // Determine colors based on state
  let bgColor, borderColor, iconColor;
  if (data.status === "failed") {
    bgColor = theme.colors.red[0];
    borderColor = theme.colors.red[5];
    iconColor = "red";
  } else if (data.isRoot) {
    bgColor = theme.colors.blue[0];
    borderColor = theme.colors.blue[5];
    iconColor = "blue";
  } else if (data.isCurrentRequest) {
    bgColor = theme.colors.green[0];
    borderColor = theme.colors.green[5];
    iconColor = "green";
  } else {
    bgColor = theme.colors.indigo[0];
    borderColor = theme.colors.indigo[4];
    iconColor = "indigo";
  }

  // A stopped agent is neither a success nor a failure, and a green tick on one misreads the
  // whole tree — the graph is where "what actually happened here" is supposed to be legible.
  const statusIcon =
    data.status === "failed" ? (
      <IconX size={12} />
    ) : data.status === "timeout" || data.status === "cancelled" ? (
      <IconAlertTriangle size={12} />
    ) : (
      <IconCheck size={12} />
    );

  const statusColor =
    data.status === "failed"
      ? "red"
      : data.status === "timeout"
      ? "yellow"
      : data.status === "cancelled"
      ? "gray"
      : "green";

  const tooltipContent = (
    <Stack gap={4} style={{ maxWidth: 280 }}>
      <Group gap="xs">
        <Text size="sm" fw={600}>
          {data.agentRole || "Root Agent"}
        </Text>
        <Badge size="xs" color={statusColor}>
          {data.status}
        </Badge>
      </Group>
      <Group gap="md">
        <Group gap={4}>
          <IconClock size={12} />
          <Text size="xs">{data.durationMs?.toLocaleString()}ms</Text>
        </Group>
        <Group gap={4}>
          <IconCoin size={12} />
          <Text size="xs">
            <NumberFormatter
              suffix=" credits"
              value={data.creditsUsed}
              thousandSeparator
            />
          </Text>
        </Group>
      </Group>
      {data.totalTreeCreditsUsed > data.creditsUsed && (
        <Text size="xs" c="dimmed">
          Tree total:{" "}
          <NumberFormatter
            value={data.totalTreeCreditsUsed}
            thousandSeparator
          />{" "}
          credits
        </Text>
      )}
      {(data.tokensIn > 0 || data.tokensOut > 0) && (
        <Text size="xs" c="dimmed">
          Tokens: {data.tokensIn?.toLocaleString()} in /{" "}
          {data.tokensOut?.toLocaleString()} out
        </Text>
      )}
      {data.errorMessage && (
        <Text size="xs" c="red">
          Error: {data.errorMessage}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        Depth: {data.depth}
      </Text>
      <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
        ID: {data.requestId}
      </Text>
    </Stack>
  );

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: borderColor, width: 8, height: 8 }}
      />
      <Tooltip label={tooltipContent} position="top" withArrow multiline w={300}>
        <Paper
          withBorder
          p="xs"
          radius="md"
          shadow="sm"
          style={{
            minWidth: 160,
            maxWidth: 200,
            backgroundColor: bgColor,
            borderColor: borderColor,
            borderWidth: data.isCurrentRequest || data.isRoot ? 2 : 1,
          }}
        >
          <Stack gap={4}>
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon size="sm" variant="light" color={iconColor}>
                <IconBrain size={14} />
              </ThemeIcon>
              <Text
                size="sm"
                fw={data.isRoot ? 700 : 500}
                truncate
                style={{ flex: 1 }}
              >
                {data.agentRole || "Root Agent"}
              </Text>
              <Badge size="xs" color={statusColor} variant="dot">
                {statusIcon}
              </Badge>
            </Group>

            <Group gap="md" wrap="nowrap">
              <Group gap={2}>
                <IconClock size={10} color="gray" />
                <Text size="xs" c="dimmed">
                  {data.durationMs < 1000
                    ? `${data.durationMs}ms`
                    : `${(data.durationMs / 1000).toFixed(1)}s`}
                </Text>
              </Group>
              <Group gap={2}>
                <IconCoin size={10} color="orange" />
                <Text size="xs" c="dimmed">
                  <NumberFormatter
                    suffix="c"
                    decimalScale={2}
                    value={data.creditsUsed / 10000}
                  />
                </Text>
              </Group>
            </Group>

            {data.depth > 0 && (
              <Badge size="xs" variant="outline" color="gray">
                Depth {data.depth}
              </Badge>
            )}

            {data.toolCalls?.length > 0 && (
              <Stack
                gap={2}
                style={{
                  borderTop: "1px solid var(--mantine-color-gray-3)",
                  paddingTop: 4,
                  marginTop: 2,
                }}
              >
                {data.toolCalls.map((tc, i) => {
                  const isFailed = tc.status === "failed";
                  const dur =
                    tc.durationMs < 1000
                      ? `${tc.durationMs}ms`
                      : `${(tc.durationMs / 1000).toFixed(1)}s`;
                  return (
                    <Group key={tc.callId || i} gap={4} wrap="nowrap">
                      <IconTool
                        size={10}
                        color={isFailed ? "red" : "gray"}
                      />
                      <Text
                        size="10px"
                        c={isFailed ? "red" : "dimmed"}
                        truncate
                        style={{ flex: 1 }}
                      >
                        {tc.toolName}
                      </Text>
                      <Text size="10px" c={isFailed ? "red" : "dimmed"}>
                        {dur}
                      </Text>
                      {isFailed && (
                        <IconX size={10} color="var(--mantine-color-red-6)" />
                      )}
                    </Group>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Tooltip>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: borderColor, width: 8, height: 8 }}
      />
    </>
  );
}

export const nodeTypes = {
  agentExecution: AgentExecutionNodeComponent,
};
